import React, { createContext, useReducer, ReactNode } from 'react';
import { AppState, AppAction } from '../types/app';
import { todayIso } from '../calendar/dates';
import { forgetWhereAt, recallWhereAt, rememberWhereAt } from './whereAt';
import { pushBackStep } from '../hooks/backStep';
import type { Tab } from '../types/app';

const initialState: AppState = {
  authStatus: 'loading',
  me: null,
  calendarDate: '',
  calendarView: 'day',
  calendarVersion: 0,
  query: '',
  openClientId: null,
  openClientTab: null,
  activeTab: 'today',
  menuOpen: false,
  settingsPanel: null,
  booking: null,
  tapChoice: null,
  timeBlock: null,
  appointmentId: null,
  toast: null,
};

export function createInitialState(): AppState {
  return { ...initialState, calendarDate: todayIso() };
}

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SESSION_CHECKING':
      return { ...state, authStatus: 'loading' };
    case 'SESSION_STARTED':
      // Signing back in after the session expired as someone else: nothing
      // of the previous account's screen may stay on show.
      if (state.me && state.me.professional.id !== action.payload.professional.id) {
        forgetWhereAt();
        return { ...createInitialState(), authStatus: 'signedIn', me: action.payload };
      }
      return {
        ...state,
        authStatus: 'signedIn',
        me: action.payload,
      };
    case 'SESSION_ENDED':
      forgetWhereAt();
      return { ...createInitialState(), authStatus: 'signedOut' };
    case 'SESSION_EXPIRED':
      // Only an open app can expire; on launch a 401 is plain "signed out".
      return state.authStatus === 'signedIn' ? { ...state, authStatus: 'expired' } : state;
    case 'SESSION_UNREACHABLE':
      return { ...state, authStatus: 'offline' };
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    case 'TOGGLE_MENU':
      return { ...state, menuOpen: !state.menuOpen };
    case 'CLOSE_MENU':
      return { ...state, menuOpen: false };
    case 'OPEN_SETTINGS_PANEL':
      return { ...state, settingsPanel: action.payload };
    case 'CLOSE_SETTINGS_PANEL':
      return { ...state, settingsPanel: null };
    case 'OPEN_BOOK_SHEET':
      return {
        ...state,
        // On the Calendar, the day she is looking at (review 2026-10-14);
        // anywhere else today, since the day the Calendar last showed is out
        // of sight there and would silently book into the wrong day.
        booking: action.payload ?? {
          date: state.activeTab === 'calendar' && state.calendarDate ? state.calendarDate : todayIso(),
          startMinute: null,
        },
        appointmentId: null,
        tapChoice: null,
        timeBlock: null,
      };
    case 'OPEN_TAP_CHOICE':
      return { ...state, tapChoice: action.payload, timeBlock: null, booking: null, appointmentId: null };
    case 'OPEN_BLOCK_SHEET':
      return { ...state, timeBlock: action.payload, tapChoice: null, booking: null, appointmentId: null };
    case 'OPEN_APPOINTMENT':
      return { ...state, appointmentId: action.payload, booking: null, tapChoice: null, timeBlock: null };
    case 'CLOSE_SHEET':
      return { ...state, booking: null, appointmentId: null, tapChoice: null, timeBlock: null };
    case 'OPEN_CLIENT':
      return { ...state, openClientId: action.payload, openClientTab: action.tab ?? null };
    case 'CLOSE_CLIENT':
      return { ...state, openClientId: null, openClientTab: null };
    case 'CLIENT_TAB_SHOWN':
      return { ...state, openClientTab: null };
    case 'SET_CALENDAR_DATE':
      return { ...state, calendarDate: action.payload };
    case 'SET_CALENDAR_VIEW':
      return { ...state, calendarView: action.payload };
    case 'APPOINTMENTS_CHANGED':
      return { ...state, calendarVersion: state.calendarVersion + 1 };
    case 'SET_QUERY':
      return { ...state, query: action.payload };
    case 'SHOW_TOAST':
      return {
        ...state,
        toast: {
          ...(typeof action.payload === 'string' ? { message: action.payload } : action.payload),
          id: (state.toast?.id ?? 0) + 1,
        },
      };
    case 'CLEAR_TOAST':
      // Only the toast that set the timer may clear it; a newer one stays up.
      return state.toast?.id === action.payload ? { ...state, toast: null } : state;
    default:
      return state;
  }
}

export const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, undefined, () => ({ ...createInitialState(), ...recallWhereAt() }));

  React.useEffect(() => {
    if (state.authStatus === 'signedIn') {
      rememberWhereAt(state);
    }
  }, [state.authStatus, state.activeTab, state.menuOpen, state.settingsPanel, state.openClientId]);

  // Each tab switch is a back step: the phone's back gesture steps back
  // through the tabs she visited (backStep.ts). A switch made by going back
  // is not a step of its own, and signing out drops the steps so far.
  const lastTab = React.useRef<Tab>(state.activeTab);
  const restoringTab = React.useRef<Tab | null>(null);
  const tabSteps = React.useRef<Array<() => void>>([]);
  React.useEffect(() => {
    const from = lastTab.current;
    lastTab.current = state.activeTab;
    if (from === state.activeTab || state.authStatus !== 'signedIn') return;
    if (restoringTab.current === state.activeTab) {
      restoringTab.current = null;
      return;
    }
    tabSteps.current.push(
      pushBackStep(() => {
        restoringTab.current = from;
        dispatch({ type: 'SET_ACTIVE_TAB', payload: from });
        return true;
      }),
    );
  }, [state.activeTab, state.authStatus]);
  React.useEffect(() => {
    if (state.authStatus !== 'signedOut') return;
    for (const release of tabSteps.current) release();
    tabSteps.current = [];
  }, [state.authStatus]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = React.useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
